/**
 * Zugriff auf die API.
 *
 * Die Anmeldung läuft über ein HttpOnly-Cookie, das der Browser von selbst
 * mitschickt -- hier gibt es deshalb keinen Token zu verwalten. `credentials`
 * muss trotzdem gesetzt sein, sonst lässt fetch das Cookie weg, sobald
 * Oberfläche und Backend im Entwicklungsbetrieb auf verschiedenen Ports
 * liegen.
 */

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }

  /** Nicht angemeldet -- die Oberfläche schickt dann zur Anmeldung. */
  get unauthorized(): boolean {
    return this.status === 401
  }

  /**
   * Es lag am Netz, nicht an der Anfrage.
   *
   * Status 0 gibt es in keiner HTTP-Antwort; er steht hier für „gar keine
   * Antwort". Daran hängt eine Entscheidung, die man nicht raten darf: was am
   * Netz scheitert, wartet im Ausgangskorb auf später — was der Server
   * ablehnt, wartet nie, sondern gehört dem Menschen gesagt.
   */
  get offline(): boolean {
    return this.status === 0
  }
}

/**
 * Ob dieser Fehlschlag am Netz lag.
 *
 * Die Frage stellt sich an mehreren Stellen, und überall hängt dieselbe
 * Entscheidung daran: **kein Netz ist kein Ausfall.** Ein Netzfehler darf
 * nichts zumachen, was gerade offen ist -- die Eingabezeile schon gar nicht,
 * denn sie ist der Weg in den Ausgangskorb.
 */
export function ohneNetz(fehler: unknown): boolean {
  return fehler instanceof ApiError && fehler.offline
}

/**
 * Ob gerade etwas durchgeht -- gemessen, nicht geglaubt.
 *
 * `navigator.onLine` ist eine freundliche Vermutung: es sagt „online", sobald
 * irgendeine Verbindung besteht. Ein WLAN, das keine Verbindung nach draußen
 * hat, ein Mobilfunknetz mit einem Balken, ein Hotspot mit Anmeldeseite --
 * alles „online", und trotzdem kommt nichts an. Umgekehrt meldet iOS den
 * Wechsel manchmal spät.
 *
 * Deshalb zählt hier, **was tatsächlich passiert ist**: jede geglückte
 * Anfrage stellt auf „Netz", jeder Netzfehler auf „kein Netz". Das ist die
 * einzige Auskunft, die nicht raten muss.
 */
let netzDa = true
const netzHoerer = new Set<() => void>()

export function netzStand(): boolean {
  // Was der Browser weiß, gilt trotzdem: schaltet jemand den Flugmodus ein,
  // steht es sofort fest und nicht erst beim nächsten Fehlschlag.
  return netzDa && navigator.onLine
}

export function netzBeobachten(bei: () => void): () => void {
  netzHoerer.add(bei)
  return () => netzHoerer.delete(bei)
}

function netzMelden(neu: boolean): void {
  if (netzDa === neu) return
  netzDa = neu
  for (const h of netzHoerer) h()
}

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '')

async function request<T>(
  path: string,
  init: RequestInit & { query?: Record<string, string | number | undefined> } = {},
): Promise<T> {
  const { query, ...rest } = init
  let url = `${BASE}${path}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, String(value))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }

  // Ohne Netz wirft `fetch` einen TypeError, dessen Text vom Browser kommt
  // („Load failed" in Safari, „Failed to fetch" in Chrome). Der stünde sonst
  // wörtlich in der Oberfläche -- und ließe sich nirgends von einer Absage des
  // Servers unterscheiden.
  let response: Response
  try {
    response = await fetch(url, {
      credentials: 'include',
      ...rest,
      headers: {
        ...(rest.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(rest.headers as Record<string, string> | undefined),
      },
    })
  } catch {
    netzMelden(false)
    throw new ApiError(0, 'Keine Verbindung zum Server.')
  }

  // Es kam etwas zurück -- egal mit welchem Status. Auch ein 404 ist ein
  // Beweis dafür, dass die Leitung steht.
  netzMelden(true)

  if (!response.ok) {
    throw new ApiError(response.status, await readError(response))
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

async function readError(response: Response): Promise<string> {
  return errorText(await response.text().catch(() => ''), response.status)
}

function errorText(body: string, status: number): string {
  try {
    const data = JSON.parse(body)
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
    // Validierungsfehler von FastAPI kommen als Liste; die erste Meldung
    // reicht, mehr als eine hilft dem Nutzer ohnehin nicht weiter.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string }
      if (first?.msg) return first.msg
    }
  } catch {
    // Antwort ohne JSON -- dann bleibt es beim allgemeinen Text unten.
  }
  if (status >= 500) return 'Der Server hat einen Fehler gemeldet.'
  return 'Das hat nicht geklappt.'
}

/**
 * Ein Bild hochladen und dabei sagen, wie weit es ist.
 *
 * Hier steht als einzige Stelle der Anwendung `XMLHttpRequest` statt `fetch`:
 * `fetch` meldet keinen Fortschritt beim Senden. Ein Rumpf als Datenstrom
 * wäre der moderne Weg, aber Safari kennt ihn nicht -- und Safari ist auf
 * dieser Reise das Hauptgerät. Unterwegs am schmalen Netz ist der Balken
 * nicht Zierde: ohne ihn sieht ein Upload von zwanzig Sekunden aus wie ein
 * Absturz.
 */
function uploadRequest<T>(
  path: string,
  blob: Blob,
  onProgress?: (anteil: number) => void,
  method: 'POST' | 'PATCH' = 'POST',
  headers: Record<string, string> = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, `${BASE}${path}`)
    xhr.withCredentials = true
    xhr.setRequestHeader('Content-Type', blob.type || 'application/octet-stream')
    for (const [name, wert] of Object.entries(headers)) {
      xhr.setRequestHeader(name, wert)
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total)
    }

    xhr.onload = () => {
      // Auch hier gilt: es kam etwas zurück, also steht die Leitung.
      netzMelden(true)
      if (xhr.status >= 200 && xhr.status < 300) {
        // Der Server hat es; der Balken darf voll sein, auch wenn das
        // letzte Fortschrittsereignis ausgeblieben ist.
        onProgress?.(1)
        try {
          resolve(JSON.parse(xhr.responseText) as T)
        } catch {
          reject(new ApiError(xhr.status, 'Die Antwort des Servers war unlesbar.'))
        }
        return
      }
      reject(new ApiError(xhr.status, errorText(xhr.responseText, xhr.status)))
    }

    xhr.onerror = () => {
      netzMelden(false)
      reject(new ApiError(0, 'Keine Verbindung zum Server.'))
    }
    xhr.onabort = () => reject(new ApiError(0, 'Das Hochladen wurde abgebrochen.'))

    xhr.send(blob)
  })
}

export const api = {
  get: <T>(path: string, query?: Record<string, string | number | undefined>) =>
    request<T>(path, { method: 'GET', query }),

  post: <T>(
    path: string,
    body?: unknown,
    query?: Record<string, string | number | undefined>,
    // Fuer Aufnahmeort und -zeit eines Videos: die stehen bei einem Bild als
    // Kopfzeilen an der Datei, und beim Video soll dieselbe Regel gelten --
    // nur dass dort der Rumpf JSON ist und nicht die Datei.
    headers?: Record<string, string>,
  ) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? '{}' : JSON.stringify(body),
      query,
      headers,
    }),

  /**
   * Ein Bild -- roh im Rumpf, kein multipart.
   *
   * Der Server erkennt den Typ am Inhalt, nicht am Content-Type; die Angabe
   * hier ist nur Höflichkeit. Ohne multipart-Rahmen ist die Anfrage außerdem
   * so groß wie die Datei und nicht größer, was auf einem Webhosting-Paket
   * mit knappem `post_max_size` zählt.
   */
  upload: <T>(
    path: string,
    blob: Blob,
    onProgress?: (anteil: number) => void,
    headers: Record<string, string> = {},
  ) => uploadRequest<T>(path, blob, onProgress, 'POST', headers),

  /**
   * Ein Stück eines Videos.
   *
   * `Content-Range` sagt, wo das Stück hingehört. Der Server prüft es gegen
   * seinen Stand und lehnt ab, was nicht passt — das ist der Unterschied
   * zwischen „weitermachen" und „Datei kaputtschreiben".
   */
  uploadChunk: <T>(
    path: string,
    blob: Blob,
    range: string,
    onProgress?: (anteil: number) => void,
  ) => uploadRequest<T>(path, blob, onProgress, 'PATCH', { 'Content-Range': range }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body === undefined ? '{}' : JSON.stringify(body),
    }),

  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
